/**
 * Dialog para atribuir tags a um contato
 *
 * Permite selecionar múltiplas tags para um contato.
 * Ao adicionar/remover tags, dispara verificação de gatilhos manuais.
 *
 * v2.0.1 - 2026-08-29 - Correção da seleção no chat:
 *   - A seleção inicial passa a depender dos IDs persistidos, não da referência do contato
 *   - Rerenders periódicos do sidebar não desfazem mais alterações locais
 *   - Checkbox passa a aplicar explicitamente o estado emitido pelo Radix
 *
 * v2.0.0 - 2026-06-03 - Redesign visual "Dark Executive":
 *   - Header refinado com linha "Para: <contato>"
 *   - Barra de busca para filtrar tags em tempo real
 *   - Seção "Selecionadas" com pílulas removíveis
 *   - Lista vertical de "Disponíveis" com bullet colorido + checkbox
 *   - Footer com "Limpar Tudo" e botões de ação
 *   - 100% baseado em tokens semânticos do design system (light/dark)
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Loader2, Settings, X, Search, Tag as TagIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TagManagementDialog } from './TagManagementDialog';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import { useQueryClient } from '@tanstack/react-query';
import { usePermissions } from '@/hooks/usePermissions';

// Logger para este módulo
const logger = createLogger('AssignTagsDialog');

interface Tag {
  id: string;
  name: string;
  color: string;
}

interface Contact {
  id: string;
  name: string | null;
  tags: Tag[];
}

interface AssignTagsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: Contact | null;
  availableTags: Tag[];
  onSuccess: () => void;
  /** ID da conversa relacionada (opcional, para contexto no gatilho) */
  conversationId?: string | null;
  /** ID do tenant para gerenciamento de tags */
  tenantId: string;
}

export function AssignTagsDialog({
  open,
  onOpenChange,
  contact,
  availableTags,
  onSuccess,
  conversationId,
  tenantId,
}: AssignTagsDialogProps) {
  const queryClient = useQueryClient();
  // v1.0.0 - Permissões granulares para Tags de Contato
  const { canView, canCreate, canEdit, canDelete } = usePermissions();
  const canViewTags = canView('contact_tags');
  const canCreateTags = canCreate('contact_tags');
  const canEditTags = canEdit('contact_tags');
  const canDeleteTags = canDelete('contact_tags');

  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // Estado para controlar modal de gerenciamento de tags
  const [managementOpen, setManagementOpen] = useState(false);

  // Assinatura estável das tags persistidas. O objeto `contact` pode ser recriado
  // a cada renderização do chat, mas a seleção só deve ser reinicializada quando
  // o contato ou os vínculos efetivamente mudarem.
  const persistedTagIdsSignature = useMemo(
    () => (contact?.tags ?? []).map((tag) => tag.id).sort().join(','),
    [contact?.tags]
  );

  // Sincroniza a seleção ao abrir, trocar de contato ou receber vínculos atualizados.
  useEffect(() => {
    if (contact && open) {
      const persistedTagIds = persistedTagIdsSignature
        ? persistedTagIdsSignature.split(',')
        : [];
      setSelectedTags(new Set(persistedTagIds));
      setSearchTerm('');
    }
  }, [contact?.id, open, persistedTagIdsSignature]);

  // Define a seleção de forma determinística; sem `checked`, alterna o estado atual.
  const handleToggleTag = (tagId: string, checked?: boolean) => {
    setSelectedTags((prev) => {
      const next = new Set(prev);
      const shouldSelect = checked ?? !next.has(tagId);

      if (shouldSelect) {
        next.add(tagId);
      } else {
        next.delete(tagId);
      }
      return next;
    });
  };

  // Limpa todas as seleções de uma vez
  const handleClearAll = () => {
    setSelectedTags(new Set());
  };

  // Filtra tags por termo de busca (case-insensitive)
  const filteredTags = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return availableTags;
    return availableTags.filter((t) => t.name.toLowerCase().includes(term));
  }, [availableTags, searchTerm]);

  // Lista de objetos Tag selecionados (para a seção "Selecionadas")
  const selectedTagObjects = useMemo(
    () => availableTags.filter((t) => selectedTags.has(t.id)),
    [availableTags, selectedTags]
  );

  /**
   * Dispara verificação de gatilhos manuais para alteração de tag
   * Chamado de forma assíncrona para não bloquear a UI
   */
  const triggerTagCheck = (tagId: string, operation: 'added' | 'removed') => {
    if (!contact) return;

    logger.info('Verificando gatilhos de tag', { contactId: contact.id, tagId, operation });

    supabase.functions
      .invoke('check-manual-triggers', {
        body: {
          event_type: 'contact_tag_changed',
          contact_id: contact.id,
          conversation_id: conversationId || null,
          tag_id: tagId,
          operation,
        },
      })
      .then(({ data, error }) => {
        if (error) {
          logger.warn('Erro ao verificar gatilhos de tag', error);
        } else {
          logger.info('Gatilhos de tag verificados', data);
        }
      })
      .catch((err) => {
        logger.warn('Exceção ao chamar check-manual-triggers', err);
      });
  };

  const handleSave = async () => {
    if (!contact) return;

    try {
      setSaving(true);

      // Obtém tags atuais
      const currentTagIds = new Set(contact.tags.map((t) => t.id));

      // Tags a adicionar
      const tagsToAdd = [...selectedTags].filter((id) => !currentTagIds.has(id));

      // Tags a remover
      const tagsToRemove = [...currentTagIds].filter((id) => !selectedTags.has(id));

      // Adiciona novas atribuições de tag de forma idempotente.
      // Evita erro 23505 caso algum cache antigo ainda tente inserir vínculo já existente.
      if (tagsToAdd.length > 0) {
        const { error: addError } = await supabase
          .from('contact_tag_assignments')
          .upsert(
            tagsToAdd.map((tagId) => ({
              contact_id: contact.id,
              tag_id: tagId,
            })),
            { onConflict: 'contact_id,tag_id', ignoreDuplicates: true }
          );

        if (addError) throw addError;

        // Dispara verificação de gatilhos para cada tag adicionada
        tagsToAdd.forEach((tagId) => triggerTagCheck(tagId, 'added'));
      }

      // Remove atribuições de tag
      if (tagsToRemove.length > 0) {
        const { error: removeError } = await supabase
          .from('contact_tag_assignments')
          .delete()
          .eq('contact_id', contact.id)
          .in('tag_id', tagsToRemove);

        if (removeError) throw removeError;

        // Dispara verificação de gatilhos para cada tag removida
        tagsToRemove.forEach((tagId) => triggerTagCheck(tagId, 'removed'));
      }

      toast.success('Tags atualizadas com sucesso');
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      logger.error('Erro ao atualizar tags', error);
      toast.error('Erro ao atualizar tags');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        {/* Removemos o close padrão para customizar header. p-0 para controlarmos paddings internos */}
        <DialogContent
          className="sm:max-w-[460px] p-0 gap-0 overflow-hidden"
          hideCloseButton
        >
          {/* ============== HEADER ============== */}
          <div className="px-6 pt-6 pb-4 flex items-start justify-between border-b border-border/60">
            <div className="min-w-0">
              <h2 className="text-xl font-semibold text-foreground tracking-tight">
                Gerenciar Tags
              </h2>
              <p className="text-sm text-muted-foreground mt-1 truncate">
                Para:{' '}
                <span className="text-foreground font-medium">
                  {contact?.name || 'Sem nome'}
                </span>
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {canViewTags && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-muted-foreground hover:text-foreground"
                  onClick={() => setManagementOpen(true)}
                  title="Criar/editar tags"
                >
                  <Settings className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-muted-foreground hover:text-foreground"
                onClick={() => onOpenChange(false)}
                title="Fechar"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* ============== SEARCH ============== */}
          {availableTags.length > 0 && (
            <div className="px-6 pt-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar tag..."
                  className="pl-9 h-10 bg-muted/40 border-border/60 focus-visible:ring-ring/30"
                />
              </div>
            </div>
          )}

          {/* ============== CONTEÚDO ============== */}
          <div className="px-6 py-4 overflow-y-auto max-h-[420px] space-y-5">
            {/* Sem tags cadastradas */}
            {availableTags.length === 0 ? (
              <div className="text-center py-10 space-y-3">
                <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                  <TagIcon className="h-5 w-5 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground text-sm">
                  Nenhuma tag cadastrada ainda.
                </p>
                {canCreateTags && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setManagementOpen(true)}
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Criar primeira tag
                  </Button>
                )}
              </div>
            ) : (
              <>
                {/* ===== Seção: Selecionadas ===== */}
                {selectedTagObjects.length > 0 && (
                  <div>
                    <h3 className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest mb-2">
                      Selecionadas ({selectedTagObjects.length})
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedTagObjects.map((tag) => (
                        <div
                          key={tag.id}
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold"
                          style={{
                            backgroundColor: `${tag.color}15`,
                            borderColor: `${tag.color}40`,
                            color: tag.color,
                          }}
                        >
                          <span
                            className="w-2 h-2 rounded-full"
                            style={{
                              backgroundColor: tag.color,
                              boxShadow: `0 0 8px ${tag.color}80`,
                            }}
                          />
                          <span>{tag.name}</span>
                          <button
                            type="button"
                             onClick={() => handleToggleTag(tag.id, false)}
                            className="opacity-70 hover:opacity-100 transition-opacity"
                            aria-label={`Remover ${tag.name}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ===== Seção: Disponíveis ===== */}
                <div>
                  <h3 className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest mb-2">
                    Disponíveis
                  </h3>
                  {filteredTags.length === 0 ? (
                    <div className="text-center py-6 text-sm text-muted-foreground">
                      Nenhuma tag corresponde a "{searchTerm}".
                    </div>
                  ) : (
                    <div className="flex flex-col gap-1">
                      {filteredTags.map((tag) => {
                        const isSelected = selectedTags.has(tag.id);
                        return (
                          <label
                            key={tag.id}
                            className={cn(
                              'flex items-center justify-between p-2.5 rounded-lg cursor-pointer transition-all border',
                              isSelected
                                ? 'bg-muted/60 border-border'
                                : 'border-transparent hover:bg-muted/40 hover:border-border/60'
                            )}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <span
                                className="w-3 h-3 rounded-full shrink-0"
                                style={{ backgroundColor: tag.color }}
                              />
                              <span className="text-sm font-medium text-foreground truncate">
                                {tag.name}
                              </span>
                            </div>
                            <Checkbox
                              checked={isSelected}
                               onCheckedChange={(checked) => handleToggleTag(tag.id, checked === true)}
                              className="shrink-0"
                            />
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* ============== FOOTER ============== */}
          <div className="px-6 py-4 bg-muted/30 border-t border-border/60 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={handleClearAll}
              disabled={saving || selectedTags.size === 0}
              className={cn(
                'text-xs font-semibold uppercase tracking-widest transition-colors',
                selectedTags.size === 0
                  ? 'text-muted-foreground/40 cursor-not-allowed'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              Limpar Tudo
            </button>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={saving}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || availableTags.length === 0}
                className="shadow-sm"
              >
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Salvar Alterações
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de gerenciamento de tags */}
      <TagManagementDialog
        open={managementOpen}
        onOpenChange={setManagementOpen}
        tenantId={tenantId}
        canCreate={canCreateTags}
        canEdit={canEditTags}
        canDelete={canDeleteTags}
        onSuccess={() => {
          // Invalida query de tags para recarregar lista atualizada
          queryClient.invalidateQueries({ queryKey: ['contact-tags', tenantId] });
          // Também chama o callback original para atualizar outros dados
          onSuccess();
        }}
      />
    </>
  );
}
